import React, { useState, useMemo } from 'react';
import { NextPage } from 'next';
import { Container } from 'styled-bootstrap-grid';
import styled from 'styled-components';
import Link from 'next/link';
import map from 'lodash/map';
import filter from 'lodash/filter';
import useTranslation from 'next-translate/useTranslation';
import useDebounce from 'react-use/lib/useDebounce';
import loadNamespaces from 'next-translate/loadNamespaces';
import {
  style,
  useBalance,
  useAuthentication,
  useGenres,
  useBanner,
  trackActions,
  useCloseToAwards,
} from '../api';
import { Style, ViewList, FilterList } from '../components/common/Icons';
import FilteredRandomTracksList from '../containers/TracksList/FilteredRandom';
import Swiper from '../containers/Swiper';
import BrowserPageTitle from '../components/common/BrowserPageTitle';
import { withAuthSync } from '../utils/auth';
import ExploreFilter, {
  useExploreQuery,
  useExploreQueryGenres,
} from '../components/ExploreFilter';
import ExploreFilterDialog from '../components/ExploreFilterDialog';
import Flex from '../components/common/Flex';
import Box from '../components/common/Box';
import { InnerLink } from '../components/common/Link';
import { BorderButton } from '../components/common/Buttons';
import { TextRegular, Text, TextBold14 } from '../components/common/Text';
import CloseToAward from '../components/Awards/CloseToAward';
import { VCoins } from '../components/common/StaticImages';
import { CenteredLoader } from '../components/common/Loader';
import { useIsDesktop } from '../style/constants';
import {
  prefetchGenres,
  createQueryCacheState,
  prefetchCountries,
} from '../utils/ssr/prefetch';
import { VirpPageContext } from './_app';

const description =
  'Discover unknown talented producers all over the world. Explore unreleased music in all genres on VIRPP. Get your music heard!Discover unknown talented producers all over the world. Explore unreleased music in all genres on VIRPP. Like or skip a track by swiping your screen, as the track gains likes, it climbs the genre charts.';

const ViewButton = styled(Flex)`
  justify-content: center;
  align-items: center;
  padding: 1.3rem 1.5rem;

  &:hover {
    cursor: pointer;
    background-color: whitesmoke;
  }
`;

const TracksListWrapper = styled.div`
  div[data-name='row'] {
    border-left: none;
    border-right: none;
  }
`;

const Explore: NextPage = () => {
  const { t } = useTranslation('profile');
  const [toggle, setToggle] = useState<'swiper' | 'list'>('swiper');

  const [showFilterModal, setShowFilterModal] = useState(false);
  const { data: closeAwards } = useCloseToAwards();

  const genres = useGenres();
  const { data: balance } = useBalance();
  const desktop = useIsDesktop();

  const { isLoggedIn } = useAuthentication();

  const { isLoading: fetchingBanner, data: banner } = useBanner();

  const query = useExploreQuery();
  const selectedGenres = useExploreQueryGenres();

  const genresSelectedString = useMemo(
    () =>
      map(
        filter(genres, ({ id }) => selectedGenres.includes(id)),
        'name',
      ).join(', ') || '',
    [genres, selectedGenres],
  );

  /**
   * Refetch tracks as when query changes
   * but debounce it to ensure we don't fire unnecessary
   * requests
   */
  useDebounce(
    () => {
      trackActions.fetchTracksFilteredRandom(query || {});
    },
    500,
    [query],
  );

  const openFilterModal = () => {
    setShowFilterModal(true);
  };

  const closeFilterModal = () => {
    setShowFilterModal(false);
  };

  return (
    <StyledContainer fluid>
      <BrowserPageTitle description={description}>
        {t('explore:browserPageTitle')}
      </BrowserPageTitle>
      <Flex
        width="100%"
        alignItems="center"
        justifyContent={['center', 'center', 'space-between']}
      >
        <Flex mr={3} alignItems="center" display={['none', 'none', 'flex']}>
          <TextRegular mr={2}>{t('explore:view')}</TextRegular>
          <ViewButton
            color={toggle === 'swiper' ? 'primary_pink_new' : 'grey_dark_new'}
            alignItems="center"
            onClick={() => setToggle('swiper')}
          >
            <Style
              size={20}
              style={{
                transform: 'rotate(180deg)',
              }}
            />
            <TextRegular ml={2}>{t('explore:swipe')}</TextRegular>
          </ViewButton>
          <ViewButton
            color={toggle === 'list' ? 'primary_pink_new' : 'grey_dark_new'}
            alignItems="center"
            onClick={() => setToggle('list')}
          >
            <ViewList size={23} />
            <TextRegular ml={2}>{t('explore:list')}</TextRegular>
          </ViewButton>
        </Flex>
        <Box display={['none', 'none', 'block']}>
          <ExploreFilter />
        </Box>
        <Box display={['block', 'block', 'none']} mt={4}>
          <ViewButton
            border="1px solid"
            color={style.grey_dark_new}
            borderColor={style.grey_light_1_new}
            p={2}
            onClick={() => openFilterModal()}
          >
            <Flex mr={['0px', '0px', 2]} alignItems="center">
              <FilterList size={23} />
              <TextRegular ml="6px">{t('explore:filter')}</TextRegular>
            </Flex>
            &nbsp;
            {genresSelectedString}
          </ViewButton>
          <ExploreFilterDialog
            onApply={closeFilterModal}
            fullScreen
            open={showFilterModal}
          />
        </Box>
      </Flex>
      <Flex width="100%" flexDirection={['column', 'column', 'row']}>
        <Box
          width="200px"
          height="fit-content"
          display={['none', 'none', 'block']}
          border="1px solid"
          borderColor={style.grey_light_1_new}
          py={2}
          px={2}
          mt={2}
          mr={4}
          textAlign="center"
        >
          {isLoggedIn ? (
            <>
              {closeAwards?.listener?.type && (
                <>
                  <TextRegular fontWeight="bold" as="h3">
                    {t('explore:nextAward')}
                  </TextRegular>
                  <CloseToAward
                    category="listener"
                    award={closeAwards.listener}
                  />
                </>
              )}
              {balance !== undefined && (
                <Flex p={3} flexDirection="column" alignItems="center">
                  <Text fontSize="12px" fontWeight="regular" color="grey">
                    {t('explore:yourVcoinBalance')}
                  </Text>
                  <Flex alignItems="center">
                    <VCoins size={24} />
                    <Text fontSize="18px" ml="8px" mb="0px">
                      {balance}
                    </Text>
                  </Flex>
                </Flex>
              )}
            </>
          ) : (
            <Flex alignItems="center" justifyContent="center" height="100%">
              <span>
                {t('explore:please')}{' '}
                <Link href="/login">{t('explore:login')}</Link>{' '}
                {t('explore:seeCloseAwards')}
              </span>
            </Flex>
          )}
        </Box>
        {toggle === 'swiper' ? (
          <Box mt="5" flex={1}>
            <Swiper
              mobileSize={250}
              filter={{ sort: 'random', ...query }}
              renderEmptyState={() => (
                <>
                  <TextBold14 style={{ marginBottom: 16, textAlign: 'center' }}>
                    {t('explore:noTracks')}
                  </TextBold14>
                  <InnerLink href="/explore" shallow>
                    <BorderButton>{t('explore:resetFilter')}</BorderButton>
                  </InnerLink>
                </>
              )}
            />
          </Box>
        ) : (
          <Box width="100%" height="100%" overflow="auto" flex={1} mt="2">
            <TracksListWrapper>
              <FilteredRandomTracksList />
            </TracksListWrapper>
          </Box>
        )}
        <Flex
          marginTop={['60px', '60px', null]}
          width={['100%', '100%', '168px']}
          height={['125px', '125px', '524px']}
          alignItems="center"
          justifyContent="center"
          backgroundColor={banner ? 'none' : style.primary_pink_new}
          color={style.white}
          textAlign="center"
          flexDirection="column"
          padding={banner ? '0px' : 3}
          ml={[0, 0, 4]}
          display={['flex', 'flex', 'flex']}
          marginBottom={['60px', '60px', '0px']}
        >
          {fetchingBanner && banner === undefined && <CenteredLoader />}
          {banner && (
            <a href={banner.url} target="_blank" rel="noopener noreferrer">
              <BannerImage
                src={desktop ? banner.banner : banner.mobile_banner}
                alt="advertising banner"
              />
            </a>
          )}
          {!fetchingBanner && banner === undefined && (
            <>
              <h2>{t('explore:yourBannerHere')}</h2>
              <TextRegular>{t('explore:contactUs')}</TextRegular>
            </>
          )}
        </Flex>
      </Flex>
    </StyledContainer>
  );
};

const BannerImage = styled.img`
  width: 100%;
  height: auto;
`;

const StyledContainer = styled(Container)`
  width: 100%;
  max-width: 1360px;
  margin: 0 auto;
`;

Explore.getInitialProps = async (ctx: VirpPageContext) => {
  const { req } = ctx;
  const getTranslations = await loadNamespaces({
    ...ctx,
    pathname: '/explore',
  });

  let props = {};

  if (req) {
    const cache = await createQueryCacheState(
      prefetchGenres,
      prefetchCountries,
    );

    props = { ...props, ...cache, ...getTranslations };
  }

  return { ...props, ...getTranslations };
};

export default withAuthSync(Explore);
